/* ============================================================
   PAPYLO — global.js
   Cart drawer, AJAX add-to-cart, product variant selection
   ============================================================ */
(function () {
  'use strict';

  const money = (cents) => {
    const fmt = window.Papylo && window.Papylo.moneyFormat;
    const value = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fmt ? fmt.replace(/\{\{\s*amount\s*\}\}/, value) : value;
  };

  /* ---------- Cart drawer ---------- */
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    overlay && overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    overlay && overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
    }
    if (e.target.closest('[data-cart-close]') || e.target.matches('[data-cart-overlay]')) {
      closeDrawer();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- Refresh cart drawer markup ---------- */
  async function refreshCart() {
    try {
      const res = await fetch('/?section_id=cart-drawer');
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('[data-cart-contents]');
      const target = document.querySelector('[data-cart-contents]');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      await updateCartCount();
    } catch (err) {
      console.error('[papylo] cart refresh failed', err);
    }
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (err) {
      /* noop */
    }
  }

  /* ---------- AJAX add to cart ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add-to-cart]');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: form.querySelector('[name="id"]').value, quantity: 1 }],
        }),
      });
      if (!res.ok) throw new Error('add failed');
      await refreshCart();
      openDrawer();
    } catch (err) {
      console.error('[papylo] add to cart failed', err);
      window.location.href = '/cart';
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = original; }
    }
  });

  /* ---------- Cart line quantity / remove ---------- */
  document.addEventListener('click', async (e) => {
    const change = e.target.closest('[data-line-change]');
    if (!change) return;
    e.preventDefault();
    const line = change.getAttribute('data-line');
    const qty = parseInt(change.getAttribute('data-line-change'), 10);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: qty }),
      });
      await refreshCart();
    } catch (err) {
      console.error('[papylo] cart change failed', err);
    }
  });

  /* ---------- Product variant selection ---------- */
  document.querySelectorAll('[data-variant-selector]').forEach((selector) => {
    const form = selector.closest('[data-product-form]') || document;
    const variants = JSON.parse(selector.getAttribute('data-variants') || '[]');
    const idInput = form.querySelector('[name="id"]');
    const priceEl = form.querySelector('[data-product-price]');
    const selected = {};

    selector.querySelectorAll('[data-option-value]').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const pos = swatch.getAttribute('data-option-position');
        selected[pos] = swatch.getAttribute('data-option-value');
        selector
          .querySelectorAll(`[data-option-position="${pos}"]`)
          .forEach((s) => s.setAttribute('aria-pressed', 'false'));
        swatch.setAttribute('aria-pressed', 'true');

        const match = variants.find((v) =>
          Object.keys(selected).every((k) => v.options[k - 1] === selected[k])
        );
        if (match) {
          if (idInput) idInput.value = match.id;
          if (priceEl) priceEl.textContent = money(match.price);
        }
      });
    });
  });

  /* ---------- Product thumbnails ---------- */
  document.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const main = document.querySelector('[data-product-main-image]');
      if (main) main.src = thumb.getAttribute('data-full') || thumb.src;
    });
  });

  /* ---------- Customer stories carousel ---------- */
  document.querySelectorAll('[data-customer-stories]').forEach((root) => {
    const track = root.querySelector('[data-stories-track]');
    const prev = root.querySelector('[data-stories-prev]');
    const next = root.querySelector('[data-stories-next]');
    if (!track) return;
    const amount = () => Math.min(track.clientWidth * 0.8, 420);
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -amount(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: amount(), behavior: 'smooth' }));

    const videos = root.querySelectorAll('[data-stories-video] video');
    if (videos.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.4 });
      videos.forEach((v) => io.observe(v));
    }
  });

  /* ---------- Video testimonials: single-audio mute toggle ---------- */
  document.querySelectorAll('[data-video-testimonials]').forEach((root) => {
    const cards = root.querySelectorAll('[data-video-card]');
    cards.forEach((card) => {
      const btn = card.querySelector('[data-video-mute-toggle]');
      const video = card.querySelector('video');
      if (!btn || !video) return;
      const muted = btn.querySelector('[data-icon-muted]');
      const unmuted = btn.querySelector('[data-icon-unmuted]');
      const sync = () => {
        if (muted) muted.hidden = !video.muted;
        if (unmuted) unmuted.hidden = video.muted;
      };
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (video.muted) {
          cards.forEach((other) => {
            if (other === card) return;
            const v = other.querySelector('video');
            if (v) v.muted = true;
          });
        }
        video.muted = !video.muted;
        cards.forEach((c) => {
          const v = c.querySelector('video');
          const b = c.querySelector('[data-video-mute-toggle]');
          if (v && b) {
            const m = b.querySelector('[data-icon-muted]');
            const u = b.querySelector('[data-icon-unmuted]');
            if (m) m.hidden = !v.muted;
            if (u) u.hidden = v.muted;
          }
        });
      });
      sync();
    });
  });

  /* ---------- Scroll reveal ---------- */
  const revealEls = document.querySelectorAll('[data-reveal]');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const ro = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    revealEls.forEach((el) => ro.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-revealed'));
  }

  /* ---------- Collection facets: auto-apply on change ---------- */
  document.querySelectorAll('[data-facet-form]').forEach((form) => {
    form.addEventListener('change', (e) => {
      // number inputs use the Apply button; checkboxes & sort auto-submit
      if (e.target.matches('input[type="number"]')) return;
      form.submit();
    });
  });

  /* ---------- Sticky mobile add-to-cart ---------- */
  const stickyBar = document.querySelector('[data-sticky-atc]');
  const stickyAdd = document.querySelector('[data-sticky-add]');
  const mainAdd = document.querySelector('[data-add-to-cart]');
  if (stickyBar && mainAdd && 'IntersectionObserver' in window) {
    const so = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    so.observe(mainAdd.closest('.product__buy') || mainAdd);
  }
  if (stickyAdd && mainAdd) {
    stickyAdd.addEventListener('click', () => mainAdd.click());
  }

  /* ---------- Italicise the first word of every display heading ---------- */
  (function italicHeadings() {
    var selector = [
      '.hero-lead h1', '.index-row h2', '.iwt__heading', '.quality-band__heading',
      '.newsletter__heading', '.stats-band__heading', '.blog__title', '.blog-lead__title',
      '.article__title', '.article-card__title', '.product__title', '.gift-card__title',
      '.feature__title', '.closing h3', '.insta__handle', '.page-head h1',
    ].join(',');

    document.querySelectorAll(selector).forEach(function (el) {
      if (el.dataset.hi) return;
      el.dataset.hi = '1';

      // find the first non-empty text node in document order
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim().length) break;
      }
      if (!node) return;

      var m = node.nodeValue.match(/^(\s*)(\S+)([\s\S]*)$/);
      if (!m) return;

      var frag = document.createDocumentFragment();
      if (m[1]) frag.appendChild(document.createTextNode(m[1]));
      var i = document.createElement('i');
      i.className = 'hi';
      i.textContent = m[2];
      frag.appendChild(i);
      if (m[3]) frag.appendChild(document.createTextNode(m[3]));
      node.parentNode.replaceChild(frag, node);
    });
  }());

  /* init */
  updateCartCount();
})();
